import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calculator } from 'lucide-react';

export default function SimulateurAmzCosmDz() {
  const [tauxChange, setTauxChange] = useState(150);
  const [commission, setCommission] = useState(15);
  const [fraisFba, setFraisFba] = useState(3);

  // Calculateur Marge
  const [prixFournisseurDA, setPrixFournisseurDA] = useState('723');
  const [pvcAmazonEUR, setPvcAmazonEUR] = useState('9.99');
  const [margeResult, setMargeResult] = useState<{
    coutFournisseurEUR: number;
    prixVenteHT: number;
    commissionEUR: number;
    fraisFBAEUR: number;
    revenusVendeur: number;
    margeNette: number;
    pourcentageMarge: number;
  } | null>(null);

  // Calculateur Prix Cible
  const [pvcAmazonCibleEUR, setPvcAmazonCibleEUR] = useState('9.99');
  const [margeSouhaitee, setMargeSouhaitee] = useState('35');
  const [prixCibleResult, setPrixCibleResult] = useState<{
    prixVenteHT: number;
    commissionEUR: number;
    fraisFBAEUR: number;
    revenusVendeur: number;
    coutMaxEUR: number;
    prixCibleDA: number;
    margeRealisee: number;
  } | null>(null);

  const formatNumber = (n: number) => n.toFixed(2);
  const formatPercent = (n: number) => n.toFixed(1) + '%';

  const calculerMarge = () => {
    const prixFournDA = parseFloat(prixFournisseurDA);
    const pvcEUR = parseFloat(pvcAmazonEUR);
    
    if (isNaN(prixFournDA) || isNaN(pvcEUR) || prixFournDA <= 0 || pvcEUR <= 0) {
      alert('Veuillez saisir des valeurs valides pour le prix fournisseur et le PVC Amazon.');
      return;
    }

    // Calculs selon formules LABONEDJMA
    const coutFournisseurEUR = prixFournDA / tauxChange;
    const prixVenteHT = pvcEUR / 1.20; // TTC → HT
    const commissionEUR = prixVenteHT * (commission / 100);
    const revenusVendeur = prixVenteHT - commissionEUR - fraisFba;
    const margeNette = revenusVendeur - coutFournisseurEUR;
    const pourcentageMarge = revenusVendeur > 0 ? (margeNette / revenusVendeur) * 100 : 0;

    setMargeResult({ 
      coutFournisseurEUR,
      prixVenteHT, 
      commissionEUR, 
      fraisFBAEUR: fraisFba, 
      revenusVendeur, 
      margeNette, 
      pourcentageMarge 
    });
  };

  const calculerPrixCible = () => {
    const pvcEUR = parseFloat(pvcAmazonCibleEUR);
    const marge = parseFloat(margeSouhaitee);
    
    if (isNaN(pvcEUR) || isNaN(marge) || pvcEUR <= 0 || marge <= 0) {
      alert('Veuillez saisir des valeurs valides pour le PVC Amazon et la marge souhaitée.');
      return;
    }

    // Calculs à rebours selon formules LABONEDJMA
    const prixVenteHT = pvcEUR / 1.20; // TTC → HT
    const commissionEUR = prixVenteHT * (commission / 100);
    const revenusVendeur = prixVenteHT - commissionEUR - fraisFba;

    // Calcul du coût fournisseur maximum pour atteindre la marge souhaitée
    const coutMaxEUR = revenusVendeur * (1 - marge / 100);
    const prixCibleDA = coutMaxEUR * tauxChange;

    // Vérification : calcul de la marge réelle
    const margeRealisee = revenusVendeur > 0 ? ((revenusVendeur - coutMaxEUR) / revenusVendeur) * 100 : 0;

    setPrixCibleResult({ 
      prixVenteHT,
      commissionEUR, 
      fraisFBAEUR: fraisFba, 
      revenusVendeur,
      coutMaxEUR,
      prixCibleDA,
      margeRealisee 
    });
  };

  // Calcul automatique au chargement
  useEffect(() => {
    calculerMarge();
    calculerPrixCible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="text-center">
        <div className="flex justify-center">
          <Badge className="bg-transparent border-2 border-orange-500 text-orange-500 font-bold px-6 py-3 text-base shadow-lg shadow-orange-500/30 animate-pulse">
            <Calculator className="w-5 h-5 mr-2" />
            Simulateur AMZ CW.Dz
          </Badge>
        </div>
      </div>

      {/* Paramètres ajustables */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#a2cc3a]">Paramètres</CardTitle>
          <CardDescription>Ajustez les paramètres de calcul selon vos besoins</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="tauxChange">Taux de change (DA/EUR)</Label>
            <Input
              id="tauxChange"
              type="number"
              value={tauxChange}
              onChange={(e) => setTauxChange(parseFloat(e.target.value) || 150)}
              step="0.1"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="commission">Commission Amazon (%)</Label>
            <Input
              id="commission"
              type="number"
              value={commission}
              onChange={(e) => setCommission(parseFloat(e.target.value) || 15)}
              step="0.1"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fraisFba">Frais FBA (EUR)</Label>
            <Input
              id="fraisFba"
              type="number"
              value={fraisFba}
              onChange={(e) => setFraisFba(parseFloat(e.target.value) || 3)}
              step="0.1"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calculateur Marge */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#a2cc3a]">Calculer la marge</CardTitle>
            <CardDescription>
              À partir du prix fournisseur DA et du PVC Amazon EUR
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prixFournisseurDA">Prix fournisseur CosmDz (DA)</Label>
              <Input
                id="prixFournisseurDA"
                type="number"
                placeholder="Ex: 723"
                value={prixFournisseurDA}
                onChange={(e) => setPrixFournisseurDA(e.target.value)}
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pvcAmazonEUR">PVC Amazon TTC (EUR)</Label>
              <Input
                id="pvcAmazonEUR"
                type="number"
                placeholder="Ex: 9.99"
                value={pvcAmazonEUR}
                onChange={(e) => setPvcAmazonEUR(e.target.value)}
                step="0.01"
              />
            </div>
            <Button onClick={calculerMarge} className="w-full">
              Calculer la marge
            </Button>
            {margeResult && (
              <div className="mt-4 p-4 bg-card border border-[#a2cc3a]/30 rounded-lg space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Coût fournisseur (EUR):</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(margeResult.coutFournisseurEUR)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Prix de vente HT (EUR):</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(margeResult.prixVenteHT)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Commission Amazon:</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(margeResult.commissionEUR)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Frais FBA:</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(margeResult.fraisFBAEUR)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Revenus vendeur:</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(margeResult.revenusVendeur)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Marge nette:</span>
                  <span className={margeResult.margeNette < 0 ? 'text-destructive' : 'text-[#a2cc3a]'}>
                    {formatNumber(margeResult.margeNette)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-orange-500">% de marge:</span>
                  <span className="text-orange-500">
                    {formatPercent(margeResult.pourcentageMarge)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calculateur Prix Cible */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#a2cc3a]">Calculer le prix cible</CardTitle>
            <CardDescription>
              À partir du PVC Amazon EUR et de la marge souhaitée
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pvcAmazonCibleEUR">PVC Amazon TTC (EUR)</Label>
              <Input
                id="pvcAmazonCibleEUR"
                type="number"
                placeholder="Ex: 9.99"
                value={pvcAmazonCibleEUR}
                onChange={(e) => setPvcAmazonCibleEUR(e.target.value)}
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="margeSouhaitee">Marge souhaitée (%)</Label>
              <Input
                id="margeSouhaitee"
                type="number"
                placeholder="Ex: 35"
                value={margeSouhaitee}
                onChange={(e) => setMargeSouhaitee(e.target.value)}
                step="0.1"
              />
            </div>
            <Button onClick={calculerPrixCible} className="w-full">
              Calculer le prix cible
            </Button>
            {prixCibleResult && (
              <div className="mt-4 p-4 bg-card border border-[#a2cc3a]/30 rounded-lg space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Prix HT (EUR):</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(prixCibleResult.prixVenteHT)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Commission Amazon:</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(prixCibleResult.commissionEUR)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Frais FBA:</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(prixCibleResult.fraisFBAEUR)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Revenus vendeur:</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(prixCibleResult.revenusVendeur)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium">Coût fournisseur max (EUR):</span>
                  <span className="text-[#a2cc3a]">
                    {formatNumber(prixCibleResult.coutMaxEUR)} EUR
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="font-medium text-orange-500"><strong>Prix cible fournisseur (DA):</strong></span>
                  <span className="text-orange-500">
                    {formatNumber(prixCibleResult.prixCibleDA)} DA
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium">Marge réalisée:</span>
                  <span className="text-[#a2cc3a]">
                    {formatPercent(prixCibleResult.margeRealisee)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
